// Schemas Zod e tipos da API de estudo (7.10).
import { z } from 'zod';
import type { StudyMethod, StudySource } from '../types';
import type { WeekCellState } from '../rules/study';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const hhmm = z.string().regex(/^\d{2}:\d{2}$/);

export const studySessionCreateSchema = z.object({
  clientId: z.string().uuid().optional(),
  languageId: z.string().uuid(),
  date: isoDate.optional(),
  method: z.enum(['app', 'cards', 'other']),
  minutes: z.number().int().min(1).max(600),
  source: z.enum(['timer', 'manual', 'review']).default('manual'),
  startedAt: z.string().datetime({ offset: true }).optional().nullable(),
  endedAt: z.string().datetime({ offset: true }).optional().nullable(),
  lessons: z.number().int().min(0).max(999).optional().nullable(),
  cards: z.number().int().min(0).max(9999).optional().nullable(),
  note: z.string().max(500).optional().nullable(),
});
export type StudySessionCreate = z.infer<typeof studySessionCreateSchema>;

export const studySessionPatchSchema = z.object({
  minutes: z.number().int().min(1).max(600).optional(),
  method: z.enum(['app', 'cards', 'other']).optional(),
  lessons: z.number().int().min(0).max(999).optional().nullable(),
  cards: z.number().int().min(0).max(9999).optional().nullable(),
  note: z.string().max(500).optional().nullable(),
});
export type StudySessionPatch = z.infer<typeof studySessionPatchSchema>;

export const languageCreateSchema = z.object({
  code: z.string().min(2).max(8),
  name: z.string().min(1).max(40),
  nativeName: z.string().min(1).max(40),
  kanji: z.string().min(1).max(2),
  dailyGoalMin: z.number().int().min(1).max(240).default(15),
  defaultMethod: z.enum(['app', 'cards', 'other']).default('app'),
  newPerDay: z.number().int().min(0).max(100).optional(),
});
export type LanguageCreate = z.infer<typeof languageCreateSchema>;

export const languagePatchSchema = z.object({
  name: z.string().min(1).max(40).optional(),
  nativeName: z.string().min(1).max(40).optional(),
  kanji: z.string().min(1).max(2).optional(),
  dailyGoalMin: z.number().int().min(1).max(240).optional(),
  defaultMethod: z.enum(['app', 'cards', 'other']).optional(),
  active: z.boolean().optional(),
  sort: z.number().int().min(0).max(99).optional(),
});
export type LanguagePatch = z.infer<typeof languagePatchSchema>;

export const studyWeekQuerySchema = z.object({ start: isoDate.optional() });
export { hhmm as hhmmSchema };

export type Language = {
  id: string;
  code: string;
  name: string;
  nativeName: string;
  kanji: string;
  dailyGoalMin: number;
  defaultMethod: StudyMethod;
  active: boolean;
  sort: number;
  startedOn: string;
  newPerDay: number | null;
};

export type StudySession = {
  id: string;
  clientId: string | null;
  languageId: string;
  date: string;
  method: StudyMethod;
  minutes: number;
  source: StudySource;
  startedAt: string | null;
  endedAt: string | null;
  lessons: number | null;
  cards: number | null;
  reviewSessionId: string | null;
  note: string | null;
};

export type StudyTodayLang = {
  id: string;
  code: string;
  name: string;
  nativeName: string;
  kanji: string;
  goal: number;
  minutes: number;
  done: boolean;
  streak: number;
  best: number;
  atRisk: boolean;
  defaultMethod: StudyMethod;
  /** "App · 15 min", "Cartões · 120 revisados", "ainda não começou" */
  methodText: string;
  lessonsToday: number;
  reviewsToday: number;
  /** app de fora (Duolingo) registrado hoje: minutos e lições somados */
  app: { done: boolean; minutes: number; lessons: number };
  cardsDue: number;
  cardsNew: number;
};

export type StudyToday = {
  date: string;
  minutes: number;
  goal: number;
  languagesDone: number;
  languagesTotal: number;
  overall: { streak: number; best: number; atRisk: boolean };
  languages: StudyTodayLang[];
  /** bloco Cartões do Estudo, na ordem das abas da tela Cartões */
  cards: { code: string; kanji: string; name: string; due: number; new: number; text: string }[];
};

export type StudyWeek = {
  start: string;
  dates: string[];
  rows: { languageId: string; code: string; name: string; cells: { date: string; state: WeekCellState; minutes: number }[] }[];
};

export type StudyStats = {
  dayNumber: number; // dias desde o começo (inclui hoje)
  perLang: {
    languageId: string;
    code: string;
    name: string;
    minutes: number;
    days: number;
    avg: number;
    cards: number;
    lessons: number;
    detail: string; // "App · média de 16 min por dia" | "Cartões · 1.420 revisões"
    best: number;
  }[];
  total: { minutes: number; days: number; avg: number; cards: number; detail: string; best: number };
};
